"use client";

import { useEffect } from "react";
import { phCaptureException } from "@/utils/analytics";
import { DISCORD_URL } from "@/lib/landing-constants";
import { Button } from "@/components/ui/button";
import { StatePanel } from "@/components/ui/state-panel";

export default function LearningCenterError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Learning Center error:", error);
    console.error("Error name:", error.name);
    console.error("Error message:", error.message);
    console.error("Error stack:", error.stack);

    phCaptureException(error, {
      boundary: "learning-center",
      digest: error.digest,
      page: "/learning-center",
      errorName: error.name,
      errorMessage: error.message,
    });
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 bg-background">
      <div className="w-full max-w-md space-y-4">
        <StatePanel
          variant="error"
          shape="page"
          title="Something went wrong"
          message="We encountered an error loading the Learning Center. This has been reported automatically."
          lastUpdate={error.digest ? `Error ID: ${error.digest}` : undefined}
        />
        {process.env.NODE_ENV !== "production" && (
          <details className="text-left p-3 bg-destructive/10 rounded-panel border border-destructive/20">
            <summary className="text-sm font-medium text-destructive cursor-pointer">
              Error Details (dev only)
            </summary>
            <pre className="mt-2 max-h-40 overflow-auto text-xs text-destructive/80 scrollbar-custom scrollbar-color-primary">
              {error.message}
              {"\n\n"}
              {error.stack}
            </pre>
          </details>
        )}
        <div className="flex gap-3 justify-center">
          <Button onClick={reset}>Try again</Button>
          <Button asChild variant="outline">
            <a href="/">Go home</a>
          </Button>
        </div>
        <div className="text-center">
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Report this on Discord for quick help
          </a>
        </div>
      </div>
    </div>
  );
}
